import { useReactTable, getCoreRowModel, flexRender, getPaginationRowModel } from '@tanstack/react-table';
import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Row, Col, Spinner } from 'react-bootstrap';
import { Modal, Button } from 'react-bootstrap';
import { ClientService } from '../../../../services/clientService';
import './candidateCuratedClients.css';
import { useNavigate } from 'react-router-dom';

export const CuratedClientsTable = ({ fetchData, candidateId, searchClientId, tableKey = 'curatedClients' }) => {
    const [paginationState, setPaginationState] = useState(() => {
        const saved = sessionStorage.getItem(`candidatePagination-${tableKey}`);
        return saved ? JSON.parse(saved) : { pageIndex: 0, pageSize: 10 };
    });
    const [showUncurateModal, setShowUncurateModal] = useState(false);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [selectedFolderId, setSelectedFolderId] = useState(null);
    const { pageIndex, pageSize } = paginationState;
    const navigate = useNavigate();

    useEffect(() => {
        setPaginationState(prev => ({ ...prev, pageIndex: 0 }));
    }, [searchClientId]);

    useEffect(() => {
        sessionStorage.setItem(`candidatePagination-${tableKey}`, JSON.stringify({ pageIndex, pageSize }));
    }, [pageIndex, pageSize, tableKey]);

    const fetchDataOptions = { candidateId, pageIndex, pageSize, searchClientId };

    const dataQuery = useQuery({
        queryKey: ['curatedClients', candidateId, searchClientId, pageIndex, pageSize],
        queryFn: () => fetchData(fetchDataOptions),
        keepPreviousData: true,
    });

    const defaultData = useMemo(() => [], []);
    const defaultColumn = useMemo(() => [{ id: 'empty', header: 'No Columns', cell: () => null }], []);

    const columns = useMemo(() => [
        {
            accessorKey: 'clientId',
            header: 'Client ID',
            cell: ({ getValue }) => (
                <span
                    style={{ color: '#F35824', textDecoration: 'none', cursor: 'pointer' }}
                    onClick={() => navigate(`/update-client?clientId=${getValue()}`)}
                >
                    {getValue()}
                </span>
            )
        },
        { accessorKey: 'folder.name', header: 'Folder' },
        {
            accessorKey: 'curatedDate',
            header: 'Curated Date',
            cell: ({ getValue }) => (getValue() ? new Date(getValue()).toDateString() : '-'),
        },
        {
            accessorKey: 'curatedBy',
            header: 'Curated By',
            cell: ({ getValue }) => getValue() || '-',
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => {
                const clientId = row.original.clientId;
                const folderId = row.original.folder?.folderId;
                const openModal = () => {
                    setSelectedClientId(clientId);
                    setSelectedFolderId(folderId);
                    setShowUncurateModal(true);
                };

                return (
                    <button
                        onClick={openModal}
                        className="btn btn-danger btn-sm"
                    >
                        Uncurate
                    </button>
                );
            }
        }
    ], [candidateId]);

    const table = useReactTable({
        data: dataQuery.data?.curatedList ?? defaultData,
        columns: columns.length ? columns : defaultColumn,
        state: { pagination: paginationState },
        onPaginationChange: setPaginationState,
        manualPagination: true,
        pageCount: dataQuery.data?.pageCount ?? 1,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const renderPageNumbers = () => {
        const totalPageCount = table.getPageCount();
        const currentPage = table.getState().pagination.pageIndex + 1;
        const pages = [];
        pages.push(1);

        if (totalPageCount > 5) {
            if (currentPage > 3) pages.push('...');
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPageCount - 1, currentPage + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (currentPage < totalPageCount - 2) pages.push('...');
            pages.push(totalPageCount);
        } else {
            for (let i = 2; i <= totalPageCount; i++) pages.push(i);
        }
        return pages;
    };

    const totalPages = table.getPageCount();
    const currentPage = table.getState().pagination.pageIndex;
    const canNext = table.getCanNextPage();
    const canPrev = table.getCanPreviousPage();

    return (
        <>
            <Modal show={showUncurateModal} onHide={() => setShowUncurateModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Uncurate</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    Are you sure you want to remove this candidate from Client
                    <strong> {selectedClientId}</strong>?
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowUncurateModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={async () => {
                            try {
                                await ClientService.uncurateCandidate(selectedClientId, candidateId, selectedFolderId);
                                setShowUncurateModal(false);
                                dataQuery.refetch();
                            } catch (error) {
                                console.error(error);
                                alert("Failed to uncurate");
                            }
                        }}
                    >
                        Uncurate
                    </Button>
                </Modal.Footer>
            </Modal>

            <div className="position-relative">
                {dataQuery.isFetching && (
                    <div className="loader-overlay">
                        <Spinner animation="border" variant="primary" />
                    </div>
                )}

                <div className="table-wrapper">
                    <table className="tables">
                        <thead>
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map(header => (
                                        <th key={header.id}>
                                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map(row => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="pagination-controls mt-3">
                    <Row className="align-items-center g-2 flex-nowrap">

                        <Col xs="auto">
                            <div className="pagination-container d-flex gap-1">
                                <button className="border rounded p-1" onClick={() => table.setPageIndex(0)} disabled={!canPrev}>{'<<'}</button>
                                <button className="border rounded p-1" onClick={() => table.previousPage()} disabled={!canPrev}>{'<'}</button>
                                {renderPageNumbers().map((page, index) => (
                                    <button
                                        key={index}
                                        className={`border rounded p-1 ${currentPage + 1 === page ? 'active' : ''}`}
                                        onClick={() => typeof page === 'number' && table.setPageIndex(page - 1)}
                                        disabled={typeof page !== 'number'}
                                    >
                                        {page}
                                    </button>
                                ))}
                                <button className="border rounded p-1" onClick={() => table.nextPage()} disabled={!canNext}>{'>'}</button>
                                <button className="border rounded p-1" onClick={() => table.setPageIndex(totalPages - 1)} disabled={!canNext}>{'>>'}</button>
                            </div>
                        </Col>

                        <Col xs="auto">
                            <div className="d-flex align-items-center gap-2">
                                <span style={{ fontSize: '15px' }}>Go to page:</span>
                                <input
                                    type="number"
                                    defaultValue={currentPage + 1}
                                    onChange={e => {
                                        const page = Number(e.target.value);
                                        if (!page || isNaN(page)) table.setPageIndex(0);
                                        else table.setPageIndex(Math.max(0, page - 1));
                                    }}
                                    className="border p-1 rounded"
                                    style={{ width: '3rem' }}
                                />
                            </div>
                        </Col>

                        <Col xs="auto">
                            <div className="d-flex align-items-center gap-2">
                                <span style={{ fontSize: '15px' }}>Show</span>
                                <select
                                    value={pageSize}
                                    onChange={e => table.setPageSize(Number(e.target.value))}
                                    className="border p-1 rounded"
                                >
                                    {[10, 20, 30, 40, 50].map(size => (
                                        <option key={size} value={size} style={{ fontSize: '15px' }}>
                                            {size}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
        </>
    );
};
